import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import GlassCard from '@/effects/ui/GlassCard';
import { Sparkles, Heart, Mountain, Palmtree, Camera, Backpack, Baby, Users, MapPin, Play, Star, Clock } from 'lucide-react';
import OceanBackground from '@/components/background/OceanBackground';

const scenes = [
  { icon: Heart, title: '情侣海岛', desc: '夕阳·沙滩·比基尼·浪漫晚餐', tag: 'couple', location: '三亚·马尔代夫', rating: 4.9, duration: '5天4晚', img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=70', color: 'from-rose-500/30 to-pink-500/10' },
  { icon: Mountain, title: '山野徒步', desc: '森林·溪谷·露营·星空', tag: 'nature', location: '川西·云南', rating: 4.8, duration: '3天2晚', img: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=70', color: 'from-emerald-500/30 to-green-500/10' },
  { icon: Camera, title: '文艺街拍', desc: '咖啡·书店·老建筑·镜头感', tag: 'photo', location: '上海·东京', rating: 4.7, duration: '2天1晚', img: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=600&q=70', color: 'from-purple-500/30 to-indigo-500/10' },
  { icon: Palmtree, title: '热带度假', desc: '度假村·SPA·泳池·鸡尾酒', tag: 'vacation', location: '巴厘岛·普吉岛', rating: 4.9, duration: '6天5晚', img: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=70', color: 'from-amber-500/30 to-yellow-500/10' },
  { icon: Backpack, title: '毕业旅行', desc: '背包·搭伙·穷游·青春不散场', tag: 'budget', location: '大理·拉萨', rating: 4.6, duration: '7天6晚', img: 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=600&q=70', color: 'from-cyan-500/30 to-blue-500/10' },
  { icon: Baby, title: '亲子乐园', desc: '游乐场·动物园·寓教于乐', tag: 'family', location: '上海迪士尼·珠海长隆', rating: 4.8, duration: '3天2晚', img: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=600&q=70', color: 'from-orange-500/30 to-red-500/10' },
  { icon: Users, title: '闺蜜出行', desc: '下午茶·买买买·美食探店', tag: 'friends', location: '首尔·曼谷', rating: 4.7, duration: '4天3晚', img: 'https://images.unsplash.com/photo-1543269664-7eef42226a21?w=600&q=70', color: 'from-pink-500/30 to-rose-500/10' },
  { icon: Star, title: '极光灯塔', desc: '极光·雪原·温泉·童话世界', tag: 'premium', location: '北欧·冰岛', rating: 5.0, duration: '8天7晚', img: 'https://images.unsplash.com/photo-1483347756197-71ef80e95f73?w=600&q=70', color: 'from-indigo-500/30 to-blue-500/10' },
];

const tags = [
  { key: 'all', label: '全部' },
  { key: 'couple', label: '情侣' },
  { key: 'nature', label: '自然' },
  { key: 'photo', label: '摄影' },
  { key: 'vacation', label: '度假' },
  { key: 'family', label: '亲子' },
  { key: 'friends', label: '闺蜜' },
  { key: 'budget', label: '穷游' },
  { key: 'premium', label: '高端' },
];

export default function Scenes() {
  const [activeTag, setActiveTag] = useState('all');
  const filtered = activeTag === 'all' ? scenes : scenes.filter(s => s.tag === activeTag);

  return (
    <div className="min-h-screen relative">
      <Navbar />
      <OceanBackground />
      <div className="relative pt-24 pb-16 px-4 max-w-6xl mx-auto z-10">
        {/* 标题 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold cb mb-2 flex items-center gap-3">
            <Sparkles className="w-7 h-7" style={{ color: '#ffd857' }} />
            精选旅行场景
          </h1>
          <p className="cd text-base">涵盖情侣出游、山野徒步、文艺街拍、毕业旅行等多种热门玩法，一键复制完整行程</p>
        </div>

        {/* 标签筛选 */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tags.map(t => (
            <button key={t.key} onClick={() => setActiveTag(t.key)}
              className={`px-4 py-2 rounded-full text-sm transition ${activeTag === t.key ? 'text-white font-medium' : 'cd hover:!cb'}`}
              style={{ background: activeTag === t.key ? 'rgba(106,179,214,0.3)' : 'rgba(106,179,214,0.08)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* 场景卡片网格 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((scene, idx) => {
            const Icon = scene.icon;
            return (
              <GlassCard key={scene.title} delay={idx * 0.06} className="group overflow-hidden p-0">
                <div className="relative h-48 overflow-hidden">
                  <img src={scene.img} alt={scene.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className={`absolute inset-0 bg-gradient-to-t ${scene.color}`} />
                  <div className="absolute bottom-3 left-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(12px)' }}>
                      <Icon className="w-4 h-4 text-white" />
                    </span>
                    <span className="text-white text-sm font-medium">{scene.title}</span>
                  </div>
                </div>
                <div className="p-4">
                  <p className="cd text-sm mb-3">{scene.desc}</p>
                  <div className="flex items-center justify-between text-xs cd">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{scene.location}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{scene.duration}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid rgba(156,200,224,0.08)' }}>
                    <span className="flex items-center gap-1 text-sm" style={{ color: '#ffd857' }}><Star className="w-3.5 h-3.5 fill-current" />{scene.rating}</span>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-white" style={{ background: 'linear-gradient(135deg, var(--glow), var(--mid))' }}>
                      <Play className="w-3 h-3" /> 查看行程
                    </button>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      </div>
    </div>
  );
}
